/**
 * Wizard validation — single source of truth for step-transition gates.
 *
 * Step components must NOT reimplement these rules locally. The page-level
 * orchestrator (AdminInvoiceCreatePage) calls ``canProceedToNextStep`` to
 * decide whether the "Weiter" button is enabled. ``Step4Preview`` calls
 * ``canSubmit`` for its own submit button gate.
 *
 * Defense-in-depth: backend Pydantic validation is the canonical authority
 * (returns 422 on violations). These helpers are UX gates only — they
 * prevent obviously-invalid payloads from being submitted.
 *
 * NOTE: ``unit_price_chf >= 0`` matches backend ``Field(..., ge=0)``.
 * Zero-priced line items are intentionally allowed (e.g. waived fees,
 * free month with a rent line kept for record-keeping).
 */

/** Reusable items validator (Step 3 + Submit). */
export function isItemsValid(items) {
  if (!Array.isArray(items) || items.length === 0) return false;
  return items.every((item) => {
    if (!item.type || !String(item.type).trim()) return false;
    const qty = parseFloat(item.quantity);
    if (!Number.isFinite(qty) || qty <= 0) return false;
    const price = parseFloat(item.unit_price_chf);
    if (!Number.isFinite(price) || price < 0) return false;
    return true;
  });
}

/** Per-item validator — returns object {type, quantity, unit_price_chf}
 * mapping field names to bool (true = invalid). Used by Step3 for
 * inline error highlighting on the offending input. */
export function getItemFieldErrors(item) {
  const errors = { type: false, quantity: false, unit_price_chf: false };
  if (!item || !item.type || !String(item.type).trim()) {
    errors.type = true;
  }
  const qty = parseFloat(item?.quantity);
  if (!Number.isFinite(qty) || qty <= 0) {
    errors.quantity = true;
  }
  const price = parseFloat(item?.unit_price_chf);
  if (!Number.isFinite(price) || price < 0) {
    errors.unit_price_chf = true;
  }
  return errors;
}

/** Returns true if "Weiter" should be enabled at the current step. */
export function canProceedToNextStep(state) {
  if (state.step === 1) {
    return Boolean(state.selectedTenant);
  }
  if (state.step === 2) {
    return (
      Boolean(state.selectedTenancy) &&
      state.billingYear >= 1900 &&
      state.billingYear <= 2100 &&
      state.billingMonth >= 1 &&
      state.billingMonth <= 12
    );
  }
  if (state.step === 3) {
    return isItemsValid(state.items);
  }
  return true; // Step 4 — submit gate is separate (canSubmit).
}

/** Returns true if Step 4 submit button should be enabled. */
export function canSubmit(state) {
  return (
    Boolean(state.selectedTenant) &&
    Boolean(state.selectedTenancy) &&
    isItemsValid(state.items) &&
    !state.submitting
  );
}

/** Map a wizard state to the POST /api/admin/invoices body shape.
 * Mirrors backend ``InvoiceCreateRequest`` exactly: tenancy_id +
 * billing_year + billing_month + items[] + optional payment_term_days
 * (T2.6.1). NO position field (backend assigns 0..N-1). NO
 * tenant_id / unit_id / room_id (backend derives from Tenancy). */
export function buildSubmitPayload(state) {
  const payload = {
    tenancy_id: state.selectedTenancy.id,
    billing_year: state.billingYear,
    billing_month: state.billingMonth,
    items: state.items.map((item) => ({
      type: String(item.type).trim(),
      description: item.description?.trim() || null,
      quantity: parseFloat(item.quantity),
      unit_price_chf: parseFloat(item.unit_price_chf),
    })),
  };
  // T2.6.1: only include payment_term_days when explicitly set by
  // the user (a number ≥ 0). Empty input / null stays out of the
  // payload so the backend default branch fires.
  if (
    typeof state.paymentTermDays === "number" &&
    Number.isFinite(state.paymentTermDays) &&
    state.paymentTermDays >= 0
  ) {
    payload.payment_term_days = state.paymentTermDays;
  }
  return payload;
}
